/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit, AfterViewInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { createDefaultGridModel, ROW_DIRTY_CLASS } from '@gh/common-internal-shared';
import { getRouteInitData, NumberTypes, queryFormEditable, State, TextTypes } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { STYLES_FLEX_COLUMN_LAYOUT, STYLES_COMPACT_TEXTAREA_EDITOR } from '@gh/core-ui';
import {
  buildEditableCheckboxColumn,
  buildEditableNumberColumn,
  buildEditableTextColumn,
  buildEditableMultilineTextColumn,
  FocusedCell,
  GridDefinition,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  rowDragMoveEventArrayFactory,
  GridComponent,
} from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';
import { negate } from 'lodash';
import { Observable } from 'rxjs';
import {
  getJobCsDetailsItemId,
  isJobCsItemQuoteChange,
  JobCsDetailsItem,
  jobCsDetailsItemFieldState,
  validateJobCsDetailsItemField,
} from '../../../resources/job-cs-details.model';
import { JobCsDetailsCommandService } from '../job-cs-details-command.service';
import {
  JobCsDetailsState,
  queryJobCsCurrentItems,
  queryJobCsFocusedItemCell,
  queryJobCsSelectedItemId,
  queryJobCsSelectedSubheadingId,
  queryJobCsSettings,
} from '../job-cs-details.state';

@Component({
  selector: 'gh-job-cs-details-item-list',
  template: require('./job-cs-details-item-list.component.html'),
  styles: [
    STYLES_FLEX_COLUMN_LAYOUT,
    STYLES_COMPACT_TEXTAREA_EDITOR
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobCsDetailsItemListComponent implements OnInit, AfterViewInit {
  @ViewChild('grid') grid: GridComponent;

  items$: Observable<JobCsDetailsItem[]>;
  selectedId$: Observable<Maybe<ID>>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  hasSelectedParent$: Observable<boolean>;
  ccClone$: Observable<boolean>;
  ccRequired$: Observable<boolean>;

  itemGridDefinition: GridDefinition;

  constructor(
    private route: ActivatedRoute,
    private state: State<JobCsDetailsState>,
    private commands: JobCsDetailsCommandService,
    private gridDefinitionFactory: GridDefinitionFactory,
    private cd: ChangeDetectorRef
  ) {}

  get editable(): boolean {
    return this.state.project(queryFormEditable);
  }

  ngOnInit(): void {
    this.itemGridDefinition = this.buildItemGridDefinition();

    this.items$ = this.state.select(queryJobCsCurrentItems);
    this.selectedId$ = this.state.select(queryJobCsSelectedItemId);
    this.focusedCell$ = this.state.select(queryJobCsFocusedItemCell);
    this.hasSelectedParent$ = this.state.select(queryJobCsSelectedSubheadingId).pipe(map$(negate(Boolean)));

    this.ccClone$ = this.state.select(queryJobCsSettings).pipe(map$((settings) => settings.ccClone));
    this.ccRequired$ = this.state.select(queryJobCsSettings).pipe(map$((settings) => settings.ccRequired));
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.grid) {
        this.grid.api?.sizeColumnsToFit();
        window.dispatchEvent(new Event('resize'));
      }
      this.cd.detectChanges();
    });
  }

  onAdd(): void {
    this.commands.itemAdd();
  }

  onRemove(item: JobCsDetailsItem): void {
    this.commands.itemRemove(item);
  }

  onSelectedChanged(itemId: ID): void {
    this.commands.itemSelect(itemId);
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  onCcCloneChange(ccClone: boolean): void {
    this.commands.setSetting('ccClone', ccClone);
  }

  onCcRequiredChange(ccRequired: boolean): void {
    this.commands.setSetting('ccRequired', ccRequired);
  }

  onBackToQuoteChanges(item: JobCsDetailsItem): void {
    this.commands.backToQuoteChanges(item);
  }

  isQuoteChange(item: JobCsDetailsItem): boolean {
    return isJobCsItemQuoteChange(item);
  }

  private moveItems(from: JobCsDetailsItem, to: JobCsDetailsItem): void {
    this.commands.itemMove(from, to);
  }

  private buildItemGridDefinition(): LayoutBasedGridDefinition {
    const { itemGridLayout } = getRouteInitData(this.route);

    const model = createDefaultGridModel({
      editable: this.state.project(queryFormEditable),
      getId: getJobCsDetailsItemId,
      validate: validateJobCsDetailsItemField,
      getCellDef: jobCsDetailsItemFieldState,
      command: (entity, name, value) => this.commands.setItemField(getJobCsDetailsItemId(entity), name, value),
    });

    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      options: <any>{
        rowSelection: 'single',
        defaultColDef: {
          sortable: false,
        },
        animateRows: true,
        onRowDragMove: rowDragMoveEventArrayFactory(this.moveItems.bind(this)),
        rowClassRules: { [ROW_DIRTY_CLASS]: ({ data }: { data: JobCsDetailsItem }) => data.isDirty },
      },
      columns: {
        name: {
          ...buildEditableTextColumn(TextTypes.Text40, model.getCellAccessor('name')),
          rowDrag: this.editable,
        },
        description: {
          ...buildEditableMultilineTextColumn(TextTypes.Text, model.getCellAccessor('description'), {
            minRows: 3,
            maxRows: 10,
          }),
        },
        isVariationRequired: buildEditableCheckboxColumn(model.getCellAccessor('isVariationRequired')),
        variationNumber: buildEditableNumberColumn(
          NumberTypes.VariationNumber,
          model.getCellAccessor('variationNumber')),
      },
      model,
      layout: itemGridLayout,
    });
  }
}
